import type { MultipleOpportunity, SingleOpportunity } from '@ps/isp-web-services-types';
import React from 'react';
import { useField } from 'react-final-form';

import type { OpportunityInput } from '@swf/core/models/sports/betSlip/types';
import {
  getIsAvailableOpportunity,
  getIsMultipleAvailableOpportunity,
  getIsSingleAvailableOpportunity
} from '@swf/core/models/sports/betSlip/utils/getIsOpportunity';
import useDisplayPriceChanges from '@swf/layouts/SportsLayout/components/BetSlip/hooks/useDisplayPriceChanges';
import useIsMultipleStartingPriceOpportunity from '@swf/layouts/SportsLayout/components/BetSlip/hooks/useIsMultipleStartingPriceOpportunity';
import useOpportunityOddsChanges from '@swf/layouts/SportsLayout/components/BetSlip/hooks/useOpportunityOddsChanges';
import getIsDefaultStartingPriceSelected from '@swf/layouts/SportsLayout/components/BetSlip/utils/getIsDefaultStartingPriceSelected';

import PriceChangeAlert from '../PriceChangeAlert/PriceChangeAlert';
import PromotionAlert from '../PromotionAlert/PromotionAlert';
import StakeError from '../Stake/components/StakeError/StakeError';

interface Props {
  opportunity: SingleOpportunity | MultipleOpportunity;
}

const StakeAlerts: React.FC<Props> = ({ opportunity }) => {
  const { input } = useField<OpportunityInput | undefined>(opportunity.id, {
    subscription: { value: true }
  });
  const { displayOpportunityPriceChanges } = useDisplayPriceChanges();
  const { haveOddsChanged, acceptedOdds } = useOpportunityOddsChanges(opportunity);
  const isMultipleStartingPriceOpportunity = useIsMultipleStartingPriceOpportunity(opportunity);

  const isAvailableOpportunity = getIsAvailableOpportunity(opportunity);
  const isSingleAvailableOpportunity = getIsSingleAvailableOpportunity(opportunity);
  const isMultipleAvailableOpportunity = getIsMultipleAvailableOpportunity(opportunity);
  const isDefaultStartingPriceSelected = getIsDefaultStartingPriceSelected(opportunity);
  const { startingPriceSelected: isStartingPriceSelected = isDefaultStartingPriceSelected } =
    input.value || {};
  let shouldDisplayPriceChange = false;

  if (isSingleAvailableOpportunity) {
    shouldDisplayPriceChange = displayOpportunityPriceChanges(
      opportunity,
      () => haveOddsChanged && !isStartingPriceSelected
    );
  } else if (isMultipleAvailableOpportunity) {
    shouldDisplayPriceChange = displayOpportunityPriceChanges(
      opportunity,
      () => haveOddsChanged && !isMultipleStartingPriceOpportunity
    );
  }

  return (
    <>
      <StakeError fieldName={opportunity.id} />
      <PromotionAlert fieldName={opportunity.id} />

      {isAvailableOpportunity && shouldDisplayPriceChange ? (
        <PriceChangeAlert acceptedOdds={acceptedOdds} currentOdds={opportunity.winRunnerOdds} />
      ) : null}
    </>
  );
};

export default StakeAlerts;
